import { type OperationId, operationIds } from "@gen/app-access-config";
import {
  CoreQuery,
  type IAdminTransaction,
  RefundCustomAmountAfterFundsReleasedF2fPaymentBody,
  RefundFullAmountAfterFundsReleasedF2fPaymentBody,
} from "@gen/app-query";
import { zodResolver } from "@hookform/resolvers/zod";
import Button from "@mui/material/Button";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import z from "zod";

import { ActionDialog } from "~/components/action_dialog";
import { TextInput } from "~/components/text_input";
import { createMoneyInputSchema, parseMoney } from "~/entities/money";
import { TransactionMoney } from "~/entities/transaction";
import { config } from "~root/config";

interface RefundAfterFundsReleasedActionProps {
  canExecuteForStatus: (operationId: OperationId) => boolean;
  transactionData: IAdminTransaction;
}

type FormOutput = {
  refund_amount: number;
};

export function RefundAfterFundsReleasedAction({
  canExecuteForStatus,
  transactionData,
}: RefundAfterFundsReleasedActionProps) {
  const transactionId = transactionData.id;
  const clientId = transactionData.client_id;
  const queryClient = useQueryClient();
  const transactionMoney = new TransactionMoney(transactionData);

  const { data: clientData } = CoreQuery.useAdmin_getClientQuery(
    clientId ?? "",
    {
      enabled: !!clientId,
    },
  );

  const [isDialogOpen, setDialogOpen] = useState(false);

  const onSuccessCommon = () => {
    if (transactionId) {
      queryClient.invalidateQueries({
        queryKey: CoreQuery.admin_getF2fTransactionQueryKey(transactionId),
      });
    }
    reset();
  };

  const {
    mutate: refundCustomAmountAfterFundsReleasedF2f,
    isPending: isRefundingCustomAmountAfterFundsReleasedF2f,
  } =
    CoreQuery.useAdmin_refundCustomAmountAfterFundsReleasedF2fTransactionMutationWithParameters(
      {
        meta: {
          success: `Refund successful. An account debit has been applied to the seller to cover the refund cost.`,
        },
        onSuccess: onSuccessCommon,
      },
    );

  const {
    mutate: refundFullAmountAfterFundsReleasedF2f,
    isPending: isRefundingFullAmountAfterFundsReleasedF2f,
  } =
    CoreQuery.useAdmin_refundFullAmountAfterFundsReleasedF2fTransactionMutationWithParameters(
      {
        meta: {
          success: `Refund successful. An account debit has been applied to the seller to cover the refund cost.`,
        },
        onSuccess: onSuccessCommon,
      },
    );

  const locale = navigator.languages?.[0] || config.localization.locale;

  const {
    control,
    handleSubmit,
    formState: { isDirty, isValid, errors },
    reset,
    watch,
  } = useForm({
    resolver: zodResolver(
      z.object({
        refund_amount: createMoneyInputSchema(
          transactionMoney.getCurrencyCode(),
          locale,
        ),
      }),
    ),
    defaultValues: { refund_amount: "" },
    mode: "onChange",
  });

  const watchedAmount = watch("refund_amount");
  let currentMoney = transactionMoney.zero();
  if (watchedAmount && !errors.refund_amount) {
    try {
      const integerAmount = parseMoney(
        watchedAmount,
        transactionMoney.getCurrencyCode(),
        locale,
      );
      if (!isNaN(integerAmount)) {
        currentMoney = transactionMoney.create(integerAmount);
      }
    } catch (err) {
      console.error("Invalid amount:", err);
    }
  }

  const onSubmit = (data: FormOutput) => {
    const centAmount = data.refund_amount.toString();

    refundCustomAmountAfterFundsReleasedF2f({
      transaction_id: transactionId,
      refundCustomAmountAfterFundsReleasedF2fPaymentBody:
        RefundCustomAmountAfterFundsReleasedF2fPaymentBody.fromJS({
          refund_amount: centAmount,
        }),
    });
  };

  const handleFullRefund = (withFees: boolean) => {
    refundFullAmountAfterFundsReleasedF2f({
      transaction_id: transactionId,
      refundFullAmountAfterFundsReleasedF2fPaymentBody:
        RefundFullAmountAfterFundsReleasedF2fPaymentBody.fromJS({
          refund_fees: withFees,
        }),
    });
  };

  const isSubmitting =
    isRefundingCustomAmountAfterFundsReleasedF2f ||
    isRefundingFullAmountAfterFundsReleasedF2f;

  if (!clientData?.refund_after_funds_released_enabled) {
    return null;
  }

  if (
    !canExecuteForStatus(
      operationIds[
        "core_admin.refundCustomAmountAfterFundsReleasedF2fTransaction"
      ],
    ) &&
    !canExecuteForStatus(
      operationIds[
        "core_admin.refundFullAmountAfterFundsReleasedF2fTransaction"
      ],
    )
  ) {
    return null;
  }

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack flexDirection="column" spacing={5}>
          <Typography variant="body1DashedDivider">
            Custom refund after funds released
          </Typography>

          {canExecuteForStatus(
            operationIds[
              "core_admin.refundCustomAmountAfterFundsReleasedF2fTransaction"
            ],
          ) && (
            <Controller
              name="refund_amount"
              control={control}
              render={({ field }) => (
                <TextInput
                  {...field}
                  label="Refund amount"
                  placeholder="Enter amount"
                  data-testid="refund-amount-input"
                  clearable
                  onClear={() => field.onChange("")}
                  fullWidth
                  error={!!errors.refund_amount}
                  helperText={errors.refund_amount?.message}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          {transactionMoney.getCurrencySymbol()}
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              )}
            />
          )}

          <Stack flexDirection="column" spacing={3}>
            {canExecuteForStatus(
              operationIds[
                "core_admin.refundCustomAmountAfterFundsReleasedF2fTransaction"
              ],
            ) && (
              <Button
                variant="contained"
                type="button"
                size="large"
                fullWidth
                disabled={!isDirty || !isValid || isSubmitting}
                onClick={() => setDialogOpen(true)}
                data-testid="confirm-refund-button"
              >
                Confirm refund after funds released
              </Button>
            )}
            {canExecuteForStatus(
              operationIds[
                "core_admin.refundFullAmountAfterFundsReleasedF2fTransaction"
              ],
            ) && (
              <>
                <Button
                  variant="text"
                  size="large"
                  fullWidth
                  disabled={isSubmitting}
                  onClick={() => handleFullRefund(true)}
                  data-testid="refund-full-with-fees-button"
                >
                  Full refund with fees (post-release)
                </Button>
                <Button
                  variant="text"
                  size="large"
                  fullWidth
                  disabled={isSubmitting}
                  onClick={() => handleFullRefund(false)}
                  data-testid="refund-full-without-fees-button"
                >
                  Full refund without fees (post-release)
                </Button>
              </>
            )}
          </Stack>
        </Stack>
      </form>

      {isDialogOpen && (
        <ActionDialog
          heading="Custom refund after funds released"
          message="Are you sure you want to proceed with this custom refund?"
          confirmButtonMessage="Yes, confirm refund after funds released"
          cancelButtonMessage="Cancel"
          isConfirming={isSubmitting}
          confirm={handleSubmit(onSubmit)}
          cancel={() => setDialogOpen(false)}
          dataTestId="custom-refund"
        >
          <Stack
            flexDirection="column"
            sx={{
              p: 3,
              border: "1px solid",
              borderColor: "gray.200",
              width: "100%",
              borderRadius: 2,
            }}
            spacing={1}
          >
            <Stack flexDirection="row" justifyContent="space-between">
              <Typography variant="body2">
                Custom refund after funds released
              </Typography>
              <Typography color="error.main">
                {currentMoney.negate().format()}
              </Typography>
            </Stack>
            <Stack flexDirection="row" justifyContent="space-between">
              <Typography variant="body2">Kept by Trustap</Typography>
              <Typography fontWeight="bold">
                {transactionMoney
                  .getBuyerPaid()
                  .subtract(currentMoney)
                  .format()}
              </Typography>
            </Stack>
          </Stack>
        </ActionDialog>
      )}
    </>
  );
}
